import { useEffect } from "react";
import { useMemoizedFn } from "ahooks";
import { useParams } from "next/navigation";
import { useSnapshot } from "valtio";
import { getTypeAppOrPc } from "utils";
import { useTaskCenterTasks } from "./useTaskCenterTasks";
import { MemberStatusCardModalCreateAble } from "@/src/components/Chat/MemberStatusCardModal";
import EdgenDrawerCallable from "@/src/components/EdgenDrawerCallable";
import {
  useChatMemberStatusCard,
  useCredits,
  useNavigateToAura,
} from "@/src/hooks";
import { rootStore } from "@/src/store";
import { LOCAL_STORAGE_KEYS } from "../config";

const CHAT_TASK_STEP_ACTIVE_SESSION_KEY = "chat_task_step_active";

const getIsTaskStepActiveFromSession = () => {
  return sessionStorage.getItem(CHAT_TASK_STEP_ACTIVE_SESSION_KEY) === "1";
};

const setIsTaskStepActiveToSession = (active: boolean) => {
  if (active) {
    sessionStorage.setItem(CHAT_TASK_STEP_ACTIVE_SESSION_KEY, "1");
    return;
  }
  sessionStorage.removeItem(CHAT_TASK_STEP_ACTIVE_SESSION_KEY);
};

/**
 * 封装发送前的 credit 前置拦截逻辑：
 * - credits <= 0 时弹出会员引导弹窗（支持任务流程优先策略）
 * - 返回 blocked=true 表示本次发送应被阻止
 * - resetTaskStep() 在有可用 credit 时重置任务流程状态
 */
export const useChatCreditCheck = () => {
  const { credits } = useCredits();
  const { isLoggedIn } = useSnapshot(rootStore.login);
  const { locale } = useParams<{ locale: string }>();
  const { memberStatus, cardProps, isAppPayingRenewLoading } =
    useChatMemberStatusCard();
  const { navigateToAura } = useNavigateToAura();

  const { unclaimedCount, hasDoableTask } = useTaskCenterTasks({
    position: "list",
    channel: getTypeAppOrPc(),
    locale,
  });

  const hasTaskOpportunity = unclaimedCount > 0 || hasDoableTask;
  const taskModalVariant =
    unclaimedCount > 0 ? "task-claimable" : "task-doable";

  const openTaskCenterFromModal = useMemoizedFn(async () => {
    EdgenDrawerCallable.end(false);
    await navigateToAura();
  });

  /**
   * 检查 credit 是否充足，不足时弹窗提示。
   * @returns `{ blocked: true }` 表示应阻止发送，`{ blocked: false }` 表示可继续。
   */
  const checkCredit = useMemoizedFn(async (): Promise<{ blocked: boolean }> => {
    if (credits && credits > 0) {
      return { blocked: false };
    }
    if (!isLoggedIn) {
      return { blocked: false };
    }

    const isTaskStepActive = getIsTaskStepActiveFromSession();

    // 展示过会员弹窗，用户没有买继续提问时，如果有未领取任务或可做任务时，优先展示任务流程
    if (hasTaskOpportunity && isTaskStepActive) {
      await MemberStatusCardModalCreateAble({
        memberStatus,
        variant: taskModalVariant,
        onPrimaryClick: openTaskCenterFromModal,
      });
    } else {
      const result = await MemberStatusCardModalCreateAble({
        memberStatus,
        ...cardProps,
        isAppPayingRenewLoading,
      });

      if (result.action === "close" && hasTaskOpportunity) {
        setIsTaskStepActiveToSession(true);
      }
    }

    return { blocked: true };
  });

  /** 有可用 credit 时调用，重置任务流程状态 */
  const resetTaskStep = useMemoizedFn(() => {
    setIsTaskStepActiveToSession(false);
  });

  // 任务机会消失时，自动重置任务流程状态
  useEffect(() => {
    if (!hasTaskOpportunity) {
      resetTaskStep();
    }
  }, [hasTaskOpportunity, resetTaskStep]);

  useEffect(() => {
    if (credits && credits > 0) {
      localStorage.removeItem(LOCAL_STORAGE_KEYS.HIDDEN_CHAT_ID);
    }
  }, [credits]);

  return {
    checkCredit,
    resetTaskStep,
  };
};
